import { useAuth } from "../contexts/AuthContext";
import { User } from "../lib/types";

// Numeric rank per role — higher = more permissions.
// Used internally for "at least" comparisons.
const STARTUP_RANK: Record<string, number> = {
  viewer:  0,
  member:  1,
  admin:   2,
  owner:   3,
};

const VC_RANK: Record<string, number> = {
  viewer: 0,
  member: 1,
  admin:  2,
  owner:  3,
};

function startupRank(role: string | undefined): number {
  return STARTUP_RANK[role ?? ""] ?? -1; 
}

function vcRank(role: string | undefined): number {
  return VC_RANK[role ?? ""] ?? -1;
}

// ---------------------------------------------------------------------------
// useRole — call inside any component to get a set of permission predicates.
// Automatically reads the current user from AuthContext.
// ---------------------------------------------------------------------------
export function useRole() {
  const { user } = useAuth();
  return buildRole(user);
}

// ---------------------------------------------------------------------------
// buildRole — same logic but accepts a User directly.
// Useful in utilities/helpers outside of React render cycles.
// ---------------------------------------------------------------------------
export function buildRole(user: User | null) {
  const role = user?.company?.role;
  const mode = user?.company?.mode;

  // -- Shared --
  const isOwner       = () => role === "owner";

  // -- Startup predicates --
  const isAdmin             = () => startupRank(role) >= STARTUP_RANK.admin && mode === "startup";
  const canSendReports      = () => startupRank(role) >= STARTUP_RANK.admin && mode === "startup";   // admin + owner
  const canModifyDraftReports      = () => startupRank(role) >= STARTUP_RANK.member && mode === "startup";   // member, admin, owner
  const canManageIntegrations = () => startupRank(role) >= STARTUP_RANK.admin && mode === "startup"; // admin + owner
  const canManageTeam       = () => startupRank(role) >= STARTUP_RANK.admin && mode === "startup";   // admin + owner (invite, edit roles, remove)
  const canManageInvestors  = () => startupRank(role) >= STARTUP_RANK.admin && mode === "startup";   // admin + owner (add investor, accept/decline requests)
  const canWriteData        = () => startupRank(role) >= STARTUP_RANK.member && mode === "startup";  // member, admin, owner
  const canRead             = () => startupRank(role) >= STARTUP_RANK.viewer && mode === "startup";  // everyone

  // -- VC predicates --
  const isVCAdmin                = () => vcRank(role) >= VC_RANK.admin && mode === "vc";        // admin + owner
  const canAddPortfolioCompany  = () => vcRank(role) >= VC_RANK.admin && mode === "vc";         // admin + owner
  const canManageSchedules      = () => vcRank(role) >= VC_RANK.admin && mode === "vc";         // admin + owner
  const canManageTemplates      = () => vcRank(role) >= VC_RANK.admin && mode === "vc";         // admin + owner
  const canManageVCTeam         = () => vcRank(role) >= VC_RANK.admin && mode === "vc";         // admin + owner
  const canCreateAdHocRequests  = () => vcRank(role) >= VC_RANK.admin && mode === "vc";         // admin + owner (member no longer allowed — see RLS migration)
  const canReadVC               = () => vcRank(role) >= VC_RANK.viewer && mode === "vc";        // everyone

  return {
    role,
    mode,

    // shared
    isOwner,

    // startup
    isAdmin,
    canSendReports,
    canModifyDraftReports,
    canManageIntegrations,
    canManageTeam,
    canManageInvestors,
    canWriteData,
    canRead,

    // vc
    isVCAdmin,
    canAddPortfolioCompany,
    canManageSchedules,
    canManageTemplates,
    canManageVCTeam,
    canCreateAdHocRequests,
    canReadVC,
  };
}